/**
 * Shared constants for card types used across the survey editor.
 * Card names are shared by CardsList, CurrentCard and the card catalog (cardCatalog.ts).
 */

export const AI_CONVERSATION_CARD_TYPES = ["ai_chat_v2", "q_llm_conversation"];

export const CARD_DESCRIPTIONS: Record<string, string> = {
  // Question cards
  q_single: "Single choice",
  q_multiple: "Multiple choice",
  q_ranking: "Ranking",
  q_matrix: "Matrix",
  q_open: "Open ended",
  q_open_multiple: "Open-ended (multiple fields)",
  ai_chat: "AI interview",
  ai_chat_v2: "AI interview",
  q_llm_conversation: "LLM conversation",
  q_image: "Image capture",
  q_image_choice: "Single choice (image)",
  q_image_choice_multiple: "Multiple choice (image)",
  q_zipcode: "Postal code",
  q_city: "City (France only)",
  q_language: "Language selection",
  q_numeric: "Numeric",
  q_rating: "Star rating",
  q_video: "Video capture",
  q_email: "Email",
  q_slider: "Slider scale",
  q_hidden: "Recode (calculated field)",
  q_nps: "NPS",
  q_audio: "Audio capture",
  // Message cards
  m_start: "Start",
  m_text: "Text",
  m_thanks: "Thanks",
  m_image: "Image",
  m_gallery: "Image gallery",
  m_video: "Video",
  m_audio: "Audio",
  // Other/Logic cards
  o_redirect: "Redirect",
  o_ab: "A/B split",
  o_cells: "Cells",
  o_loop: "Loop / iteration",
  o_typing: "Typing indicator",
  o_url_redirect: "URL redirect",
  o_quota: "Quota check",
  o_pause: "Pause flow",
  o_custom: "Custom function",
  o_random_blocks: "Block randomization",
  o_user_attributes: "Custom attribute",
  o_reward: "Reward amount",
  // Standard cards
  s_gdpr: "GDPR notice",
  s_thanks: "Thank you message",
  s_goodbye: "Goodbye message",
  s_gender: "Gender question",
  s_age: "Age range question",
  s_zipcode: "Postal code question",
  s_csp: "CSP question",
  s_income: "Income question",
  s_satisfaction: "Satisfaction questions",
  s_optin: "Opt-in to Episto community",
  s_ai_consent: "AI interview consent",
};

export const cardTypeName = (key?: string, fallback?: string): string =>
  (key && CARD_DESCRIPTIONS[key]) || fallback || key || "";

export interface GroupColors {
  bg: string;
  text: string;
}

const QUESTION_COLORS: GroupColors = { bg: "bg-status-info-soft", text: "text-status-info-strong" };
const NEUTRAL_COLORS: GroupColors = { bg: "bg-secondary", text: "text-muted-foreground" };

export const GROUP_COLORS: Record<string, GroupColors> = {
  question: QUESTION_COLORS,
  recodes: NEUTRAL_COLORS,
  message: NEUTRAL_COLORS,
  other: NEUTRAL_COLORS,
  standard: NEUTRAL_COLORS,
};

export const DEFAULT_GROUP_COLORS: GroupColors = NEUTRAL_COLORS;

export function getGroupColors(groupType: string): GroupColors {
  return GROUP_COLORS[groupType] || DEFAULT_GROUP_COLORS;
}

// A recode is a q_hidden (group "question" for the API) but is offered and displayed under "Recodes".
export function getCardGroupColors(card: { type?: string; groupType?: string | null } | null | undefined): GroupColors {
  return getGroupColors(card?.type === "q_hidden" ? "recodes" : (card?.groupType ?? ""));
}
